import React, { useState, useMemo, useEffect } from 'react';
import { todayStr } from '@/lib/time';
import { HrxSyncResult } from '@/lib/storage';

export interface HrxSyncPanelProps {
  onSync: (body: {
    hrxCookie?: string;
    startsAt: string;
    endsAt: string;
    personnelId?: string;
    includeBreaks?: boolean;
    dryRun?: boolean;
  }) => Promise<HrxSyncResult | null>;
  defaultPersonnelId?: string;     // HRX personnel id, pre-filled if known
  defaultCookie?: string;          // pre-fill from settings if stored
}

// Mirrors the server's /api/hrx-session GET response. Raw accessToken is
// NEVER surfaced to the client — only metadata.
interface CachedSession {
  exists: boolean;
  personnelId?: string;
  expiresAt?: string;
  expiresInMs?: number;
  expired?: boolean;
  updatedAt?: string;
}

// Quick math for "HH:MM" -> decimal hours (mirror of sync-hrx.page.ts).
function fmtHours(hours: number): string {
  if (!Number.isFinite(hours) || hours <= 0) return '0h 0m';
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return `${h}h ${m}m`;
}

// "7h 42m" / "42m" / "expired"
function fmtCountdown(ms: number): string {
  if (ms <= 0) return 'expired';
  const totalMin = Math.floor(ms / (60 * 1000));
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

const HrxSyncPanel: React.FC<HrxSyncPanelProps> = ({ onSync, defaultPersonnelId, defaultCookie }) => {
  // Pre-fill cookie/personnelId if the caller has stored them. We hold them
  // in component state so refreshing the page clears them — a deliberate
  // safety net: HRX accessTokens are 8h JWTs, persisting them in localStorage
  // would leak if the device is shared. If you want persistence, add a
  // "remember for this device" checkbox that gates writing to localStorage.
  const [cookie, setCookie] = useState(defaultCookie ?? '');
  const [personnelId, setPersonnelId] = useState(defaultPersonnelId ?? '');
  const [startsAt, setStartsAt] = useState('2026-06-16');
  const [endsAt, setEndsAt] = useState(todayStr());
  const [includeBreaks, setIncludeBreaks] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<HrxSyncResult | null>(null);
  const [dryRan, setDryRan] = useState(false);

  // Server-side cached HRX session state. Fetched on mount so the panel
  // can show "Cached HRX session expires in 7h 42m" without re-asking the
  // user to paste. The raw token stays server-side — only metadata here.
  const [cachedSession, setCachedSession] = useState<CachedSession | null>(null);
  const [cacheTick, setCacheTick] = useState(0); // forces re-render every minute for countdown

  // The actual storage key the app uses for the active passcode (set on
  // login, read by hooks.ts). Don't hardcode a wrong key — it makes the
  // cache POST 400 with "passcode required".
  const activePasscodeKey = 'app_active_passcode';
  const getActivePasscode = (): string =>
    (typeof window !== 'undefined' ? localStorage.getItem(activePasscodeKey) : '') ?? '';

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch('/api/hrx-session', {
          headers: { 'x-passcode': getActivePasscode() },
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (!cancelled) setCachedSession(data);
      } catch {
        /* offline / not yet logged in — show no cache */
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);
  // Tick the countdown every 60s so the "expires in X" stays fresh without
  // re-fetching. Cheap.
  useEffect(() => {
    const t = setInterval(() => setCacheTick((n) => n + 1), 60 * 1000);
    return () => clearInterval(t);
  }, []);
  // Cache tick is just to re-render the countdown label.
  void cacheTick;

  // When a cached session exists and the user hasn't pasted anything,
  // use cached credentials implicitly. The cookie input shows a masked
  // placeholder + "cached" hint so the user knows what's happening.
  const usingCache = !cookie.trim() && !!cachedSession?.exists && !cachedSession.expired;
  const effectivePersonnelId = personnelId.trim() || cachedSession?.personnelId || '';
  const canSubmit = (cookie.trim() || usingCache) && effectivePersonnelId && startsAt && endsAt && !busy;

  // Range sanity — HRX is fine with wide ranges but a multi-year pull can
  // hit the 50-page safety cap silently. Surface that here so the user knows.
  const dayCount = useMemo(() => {
    const s = new Date(startsAt);
    const e = new Date(endsAt);
    if (isNaN(s.getTime()) || isNaN(e.getTime())) return 0;
    return Math.max(0, Math.round((e.getTime() - s.getTime()) / (24 * 60 * 60 * 1000)) + 1);
  }, [startsAt, endsAt]);

  const callSync = async (dryRun: boolean) => {
    if (!canSubmit && !dryRun) return;
    setError(null);
    setResult(null);
    setBusy(true);
    setDryRan(dryRun);
    try {
      // Empty cookie is intentional — it tells the server "fall back to
      // the cached HrxSession for this passcode." Empty personnelId same.
      // The server returns a clear 400 if there's no cache either.
      const res = await onSync({
        hrxCookie: cookie.trim() || undefined,
        startsAt,
        endsAt,
        personnelId: personnelId.trim() || undefined,
        includeBreaks,
        dryRun,
      });
      if (!res) {
        setError('No response from server.');
        return;
      }
      if (res.error) {
        setError(`${res.error}${res.detail ? `: ${res.detail}` : ''}`);
        return;
      }
      // Refresh cache metadata in case the run touched it (server-side
      // expiry doesn't change but the UI wants to show the same source).
      setResult(res);
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="glass-panel p-6 space-y-4 border-accent/10">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-display font-semibold tracking-tight text-foreground">
            Sync from HRX
          </h2>
          <p className="text-xs text-foreground/50 font-mono mt-0.5">
            PULL DTR SESSIONS FROM api-hrx.arrowsoft.cloud AND REPLACE THE TIMELOG
          </p>
        </div>
      </div>

      {/* Cached-session banner — surfaces what's happening with auto-fill. */}
      {cachedSession?.exists && (
        <div className={`px-3 py-2 rounded border text-xs font-mono ${
          cachedSession.expired
            ? 'bg-red-500/10 border-red-500/20 text-red-400'
            : 'bg-accent/5 border-accent/20 text-accent'
        }`}>
          {cachedSession.expired ? (
            <>⚠ Cached HRX session expired ({cachedSession.expiresAt}). Paste a fresh token below to refresh.</>
          ) : (
            <>
              ✓ Cached HRX session active — expires in{' '}
              <strong>{fmtCountdown(cachedSession.expiresInMs ?? 0)}</strong>{' '}
              ({cachedSession.expiresAt})
              {!cookie.trim() && (
                <span className="text-foreground/40 ml-2">
                  — leaving the field blank uses this cache automatically.
                </span>
              )}
            </>
          )}
          {cachedSession.personnelId && !personnelId.trim() && (
            <span className="text-foreground/40 ml-2">
              Personnel ID pre-filled from cache.
            </span>
          )}
        </div>
      )}

      {/* Inputs row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex flex-col">
          <label className="text-[10px] uppercase font-mono tracking-wider text-foreground/40 mb-1">
            HRX accessToken (Cookie)
          </label>
          <input
            type="password"
            value={cookie}
            onChange={(e) => setCookie(e.target.value)}
            placeholder={
              usingCache
                ? '•••••••• (using cached session)'
                : '67e4d033-9bb8-4705-8ac4-64334a131ffa'
            }
            className="bg-background border border-card-border rounded px-3 py-1.5 text-sm font-mono text-foreground focus:outline-none focus:border-accent/40"
            autoComplete="off"
          />
          <span className="text-[10px] text-foreground/30 font-mono mt-1">
            8h JWT from <code>POST /api/auth/login</code>.
            {cookie.trim() ? (
              <>
                {' '}Will be saved server-side for this passcode.{' '}
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await fetch('/api/hrx-session', {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'x-passcode': getActivePasscode(),
                        },
                        body: JSON.stringify({
                          accessToken: cookie.trim(),
                          personnelId: effectivePersonnelId,
                        }),
                      });
                      // Re-fetch metadata so the banner updates.
                      const res = await fetch('/api/hrx-session', {
                        headers: { 'x-passcode': getActivePasscode() },
                      });
                      if (res.ok) setCachedSession(await res.json());
                    } catch {
                      /* save failed — keep cookie in input, ignore */
                    }
                  }}
                  className="text-accent hover:text-accent/80 underline cursor-pointer"
                >
                  Save to cache
                </button>
              </>
            ) : usingCache ? (
              <> Using cached session. Paste a new value to replace.</>
            ) : (
              <> Not persisted unless you click Save.</>
            )}
          </span>
        </div>

        <div className="flex flex-col">
          <label className="text-[10px] uppercase font-mono tracking-wider text-foreground/40 mb-1">
            HRX personnelId
          </label>
          <input
            type="text"
            value={personnelId}
            onChange={(e) => setPersonnelId(e.target.value)}
            placeholder="cmqege8ax00106nog4v2x0bw2"
            className="bg-background border border-card-border rounded px-3 py-1.5 text-sm font-mono text-foreground focus:outline-none focus:border-accent/40"
          />
          <span className="text-[10px] text-foreground/30 font-mono mt-1">
            <code>GET /api/auth/me</code> returns this as <code>id</code> for your user.
          </span>
        </div>

        <div className="flex flex-col">
          <label className="text-[10px] uppercase font-mono tracking-wider text-foreground/40 mb-1">
            startsAt (PHT)
          </label>
          <input
            type="date"
            value={startsAt}
            onChange={(e) => setStartsAt(e.target.value)}
            className="bg-background border border-card-border rounded px-3 py-1.5 text-sm font-mono text-foreground focus:outline-none focus:border-accent/40"
          />
        </div>

        <div className="flex flex-col">
          <label className="text-[10px] uppercase font-mono tracking-wider text-foreground/40 mb-1">
            endsAt (PHT)
          </label>
          <input
            type="date"
            value={endsAt}
            onChange={(e) => setEndsAt(e.target.value)}
            className="bg-background border border-card-border rounded px-3 py-1.5 text-sm font-mono text-foreground focus:outline-none focus:border-accent/40"
          />
          <span className="text-[10px] text-foreground/30 font-mono mt-1">
            Range: {dayCount} day{dayCount === 1 ? '' : 's'}
          </span>
        </div>
      </div>

      {/* Toggles + Actions */}
      <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
        <label className="flex items-center gap-2 cursor-pointer select-none group">
          <input
            type="checkbox"
            checked={includeBreaks}
            onChange={(e) => setIncludeBreaks(e.target.checked)}
            className="accent-accent w-3.5 h-3.5 cursor-pointer"
          />
          <span className={`text-[10px] font-mono uppercase tracking-wider transition-colors ${
            includeBreaks ? 'text-accent' : 'text-foreground/40 group-hover:text-foreground/60'
          }`}>
            Include Breaks (off by default — break time doesn't count as work)
          </span>
        </label>

        <div className="flex items-center gap-3">
          <button
            onClick={() => callSync(true)}
            disabled={!canSubmit}
            className="px-4 py-2 text-xs font-mono uppercase tracking-wider text-foreground/60 hover:text-foreground hover:bg-foreground/5 rounded transition-all disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
          >
            {busy && dryRan ? 'Fetching…' : 'Preview (Dry Run)'}
          </button>
          <button
            onClick={() => callSync(false)}
            disabled={!canSubmit}
            className="px-6 py-2 text-xs font-mono uppercase tracking-wider text-background bg-accent hover:bg-accent/90 border border-accent/20 rounded font-semibold transition-all shadow-md shadow-accent/10 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
          >
            {busy && !dryRan ? 'Syncing…' : 'Execute Sync'}
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded text-red-400 text-xs font-mono">
          [ERROR] {error}
        </div>
      )}

      {result && (
        <div className="border-t border-card-border pt-4 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 font-mono text-xs">
            <div className="bg-background/30 border border-card-border rounded p-3">
              <div className="text-[10px] uppercase text-foreground/40 tracking-wider">Sessions Found</div>
              <div className="text-lg text-foreground">{result.sessionsFound}</div>
            </div>
            <div className="bg-background/30 border border-card-border rounded p-3">
              <div className="text-[10px] uppercase text-foreground/40 tracking-wider">Sessions Kept</div>
              <div className="text-lg text-accent">{result.sessionsKept}</div>
            </div>
            <div className="bg-background/30 border border-card-border rounded p-3">
              <div className="text-[10px] uppercase text-foreground/40 tracking-wider">Sessions Skipped</div>
              <div className="text-lg text-foreground/60">{result.sessionsSkipped}</div>
            </div>
            <div className="bg-background/30 border border-card-border rounded p-3">
              <div className="text-[10px] uppercase text-foreground/40 tracking-wider">
                {result.dryRun ? 'Preview Total' : 'Total Hours'}
              </div>
              <div className="text-lg text-accent">{fmtHours(result.totalHours)}</div>
            </div>
          </div>

          {result.dryRun && (
            <div className="text-[10px] font-mono uppercase tracking-wider text-foreground/40">
              PREVIEW ONLY — NO CHANGES WRITTEN. CLICK EXECUTE SYNC TO COMMIT.
              {result.authSource === 'cache' && (
                <span className="ml-2 text-accent">[used cached session]</span>
              )}
              {result.authSource === 'body' && (
                <span className="ml-2 text-foreground/40">[used fresh paste]</span>
              )}
            </div>
          )}

          {result.success && !result.dryRun && (
            <div className="text-[10px] font-mono uppercase tracking-wider text-accent">
              SYNC COMMITTED — REFRESH THE LOG TAB TO SEE THE MERGED ENTRIES.
              {result.authSource === 'cache' && (
                <span className="ml-2 text-foreground/40">[used cached session]</span>
              )}
              {result.authSource === 'body' && (
                <span className="ml-2 text-foreground/40">[used fresh paste — remember to click Save to cache it]</span>
              )}
            </div>
          )}

          {/* Day breakdown — limit to ~20 rows for sanity */}
          {Object.keys(result.dayMap).length > 0 && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-foreground/40 mb-2">
                Per-day totals
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                {Object.entries(result.dayMap)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .slice(0, 60)
                  .map(([date, hours]) => (
                    <div key={date} className="flex justify-between items-center px-3 py-2 bg-background/30 border border-card-border rounded font-mono text-xs">
                      <span className="text-foreground/60">{date}</span>
                      <span className="text-accent">{fmtHours(hours)}</span>
                    </div>
                  ))}
              </div>
              {Object.keys(result.dayMap).length > 60 && (
                <div className="text-[10px] text-foreground/40 font-mono mt-2">
                  …and {Object.keys(result.dayMap).length - 60} more
                </div>
              )}
            </div>
          )}

          {result.skipped.length > 0 && (
            <details className="text-xs font-mono">
              <summary className="cursor-pointer text-foreground/40 hover:text-foreground/60">
                {result.skipped.length} break session(s) skipped
              </summary>
              <div className="mt-2 space-y-1 pl-4 text-foreground/50">
                {result.skipped.map((s) => (
                  <div key={s.id}>
                    {s.startsAt} → {s.endsAt} ({s.task})
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
};

export default HrxSyncPanel;